import React from 'react';
import { toast } from 'sonner';
import { SectionCard } from '../common/SectionCard';
import { Button } from '../ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/Table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { usePlatform } from '../../contexts/PlatformContext';
import { STAGE_META, MEGA_MAJOR_STAGES, INTERNATIONAL_STAGES } from '../../utils/stages';
import type { StageId } from '../../types/domain';
const stageOptions: StageId[] = Array.from(new Set([...MEGA_MAJOR_STAGES, ...INTERNATIONAL_STAGES]));
export function RecommendationLibrary() {
  const {
    rules,
    toggleRule
  } = usePlatform();
  const [stage, setStage] = React.useState<'all' | StageId>('all');
  const filtered = rules.filter(rule => stage === 'all' || rule.stage === stage);
  const activeCount = rules.filter(rule => rule.active).length;
  const totalTriggers = rules.reduce((sum, rule) => sum + rule.triggers, 0);
  const meanAcceptance = Math.round(rules.reduce((sum, rule) => sum + rule.acceptanceRate, 0) / rules.length);
  return <div className="space-y-4" data-id="element-111" data-mp="3givt24p4m64c">
      <div className="grid gap-3 sm:grid-cols-3" data-id="element-112" data-mp="3v81xq86qgojm">
        <div className="rounded-sm border border-border bg-card px-4 py-3" data-id="element-113" data-mp="3v7ihqx0n8a88">
          <p className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-114" data-mp="3q8p5t9108ra6">
            Active rules
          </p>
          <p className="mt-1 font-mono text-xl font-semibold" data-id="element-115" data-mp="32jwczlfoizjs">
            {activeCount} / {rules.length}
          </p>
        </div>
        <div className="rounded-sm border border-border bg-card px-4 py-3" data-id="element-116" data-mp="3bk2ox2vqbrgu">
          <p className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-117" data-mp="3bldpyuxsil49">
            Triggers this year
          </p>
          <p className="mt-1 font-mono text-xl font-semibold" data-id="element-118" data-mp="qtk0lh4on22b">{totalTriggers}</p>
        </div>
        <div className="rounded-sm border border-border bg-card px-4 py-3" data-id="element-119" data-mp="ms8h7tcejd2k">
          <p className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-120" data-mp="mub78ddk167a">
            Mean officer acceptance
          </p>
          <p className="mt-1 font-mono text-xl font-semibold" data-id="element-121" data-mp="3kla8cz3pmhpa">{meanAcceptance}%</p>
        </div>
      </div>

      <SectionCard title="Preset recommendation rules" description="Each rule maps a condition on a record to a recommendation and a template action, tagged to the stage where it applies." actions={<Select value={stage} onValueChange={value => setStage(value as 'all' | StageId)} data-id="element-123" data-mp="2wxse8wwz37j5">
            <SelectTrigger className="w-[190px]" aria-label="Filter rules by stage" data-id="element-124" data-mp="31w3kkxo26zwm">
              <SelectValue data-id="element-125" data-mp="35vukprzh6sdt" />
            </SelectTrigger>
            <SelectContent data-id="element-126" data-mp="3anm1jastxjqr">
              <SelectItem value="all" data-id="element-127" data-mp="3anvt32tivyyk">All stages</SelectItem>
              {stageOptions.map(stageId => <SelectItem key={stageId} value={stageId} data-id="element-128" data-mp="2e4oi94wa8ild">
                  {STAGE_META[stageId].label}
                </SelectItem>)}
            </SelectContent>
          </Select>} bodyClassName="p-0" data-id="element-122" data-mp="3pkmxjqi200v1">
        <div className="overflow-x-auto" data-id="element-129" data-mp="2alpnfcpmwims">
          <Table data-id="element-130" data-mp="25o7fnpncs9gd">
            <TableHeader data-id="element-131" data-mp="25ps91bxxlmcn">
              <TableRow data-id="element-132" data-mp="25piedb1xyr0m">
                <TableHead className="w-[90px]" data-id="element-133" data-mp="21n3y8ypnq5d1">Rule</TableHead>
                <TableHead className="min-w-[260px]" data-id="element-134" data-mp="21lkgt4s9lo9o">Condition</TableHead>
                <TableHead className="min-w-[260px]" data-id="element-135" data-mp="1wmzft356v0k6">Recommendation & action</TableHead>
                <TableHead data-id="element-136" data-mp="1rp7oobcdbih1">Stage</TableHead>
                <TableHead className="text-right" data-id="element-137" data-mp="1rq90wj54eb0c">Triggers</TableHead>
                <TableHead className="text-right" data-id="element-138" data-mp="1mx6ecuyrg1tn">Accepted</TableHead>
                <TableHead className="text-right" data-id="element-139" data-mp="1hyv3cgwqbl9u">State</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-id="element-140" data-mp="1dyccoq6kol98">
              {filtered.map(rule => <TableRow key={rule.id} data-id="element-141" data-mp="18ygi1hjrm66c">
                  <TableCell className="font-mono text-[11px] text-muted-foreground" data-id="element-142" data-mp="18xevogxatx9b">
                    {rule.id}
                  </TableCell>
                  <TableCell className="text-[12px] leading-relaxed" data-id="element-143" data-mp="141ps82i5rw7q">{rule.condition}</TableCell>
                  <TableCell data-id="element-144" data-mp="343z27q53fyqo">
                    <p className="text-[12px] leading-relaxed" data-id="element-145" data-mp="3917n3yhmmgvm">{rule.recommendation}</p>
                    <p className="mt-1 text-[11px] text-muted-foreground" data-id="element-146" data-mp="2uc32ybr9u2kv">→ {rule.action}</p>
                  </TableCell>
                  <TableCell className="text-[12px]" data-id="element-147" data-mp="2za4kutcynvur">{STAGE_META[rule.stage].label}</TableCell>
                  <TableCell className="text-right font-mono text-[12px]" data-id="element-148" data-mp="2lavhwvdrmy8q">
                    {rule.triggers}
                  </TableCell>
                  <TableCell className="text-right font-mono text-[12px]" data-id="element-149" data-mp="2pe1l1u5l09az">
                    {rule.acceptanceRate}%
                  </TableCell>
                  <TableCell className="text-right" data-id="element-150" data-mp="2gbijdddj82qb">
                    <Button variant={rule.active ? 'secondary' : 'outline'} size="xs" onClick={() => {
                  toggleRule(rule.id);
                  toast.success(`${rule.id} ${rule.active ? 'paused' : 'activated'}`, {
                    description: rule.recommendation
                  });
                }} data-id="element-151" data-mp="2gcagisms28ip">
                      {rule.active ? 'Active' : 'Paused'}
                    </Button>
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>
      </SectionCard>
    </div>;
}